﻿import React, { Fragment, useState, useEffect, useRef } from 'react';
import { FormDateRangeInput } from "components/form-fields";
import { ListingTable } from "components/listing";
import { ShowHide } from "components/layout";
import { DateToLocal } from "components/dates";
import PropTypes from 'prop-types';
import moment from "moment";
import { useSelector, useDispatch } from 'react-redux';
import { SetFiltering } from 'state/actions';
import classNames from 'classnames';

const rows25 = 25;
const rows50 = 50;
const rows100 = 100;

const perPageOptions = [rows25, rows50, rows100];

const defaultArray = [];

export function AuditingTable(
    {
        data = defaultArray,
        totalCount = 0,
        noDataMessage = "",
        dataIsFormatted = false,
        tableHeaders = ["Date", "User", "Setting", "Old Value", "New Value"],
        hideHeader = false,
        loading = false,
        pageSize = 25,
        allowClear = true,
        heading = "History",
        hideDatesAndPaging = false,
        startDate = null,
        endDate = null,
        initialStartDate = null,
        initialEndDate = null,
        onChange
    }
) {
    const reduxAuditingHistoryName = "auditingDates";
    const dispatch = useDispatch();

    const reduxSelectedDates = useSelector(state => state.filters[reduxAuditingHistoryName]);

    const [tableOptions, setTableOptions] = useState({
        startDate: initialStartDate === null ? moment().format("YYYY-MM-DD") : initialStartDate,
        endDate: initialEndDate === null ? moment().format("YYYY-MM-DD") : initialEndDate,
        pageNumber: 1,
        pageSize: pageSize
    });
    const optionsRef = useRef(tableOptions);
    const onChangeRef = useRef(onChange);

    const [loadingData, setLoadingData] = useState(true);
    const [tableRows, setTableRows] = useState([]);
    const [noDataMessageValue, setNoDataMessage] = useState(noDataMessage);

    useEffect(() => {
        if (startDate != null && endDate != null
            && (reduxSelectedDates?.startDate !== startDate || reduxSelectedDates?.endDate !== endDate)) {
            tableOptions.startDate = startDate;
            tableOptions.endDate = endDate;
            dispatch(SetFiltering(reduxAuditingHistoryName,
                {
                    startDate: startDate,
                    endDate: endDate
                }
            ));
        } else if (reduxSelectedDates?.startDate != null) {
            tableOptions.startDate = reduxSelectedDates?.startDate;
            tableOptions.endDate = reduxSelectedDates?.endDate;
        }
    }, [reduxSelectedDates]);

    useEffect(() => {
        if (loadingData) {
            onChangeRef.current(optionsRef.current.pageSize, optionsRef.current.pageNumber, optionsRef.current.startDate, optionsRef.current.endDate);
        }

    }, [loadingData]);

    useEffect(() => {

        if (data != null) {

            let dataValue = [];

            if (!dataIsFormatted) {
                dataValue = data.map((d) => {
                    return [<DateToLocal key={`date-${d.date}`}>{d.date}</DateToLocal>, d.username, d.propertyFriendlyName,
                        (d.dataType === 'dateTime') ? <DateToLocal inputFormat={"DD/MM/YYYY HH:mm"}>{d.oldValue}</DateToLocal> : d.oldValue,
                        (d.dataType === 'dateTime') ? <DateToLocal inputFormat={"DD/MM/YYYY HH:mm"}>{d.newValue}</DateToLocal> : d.newValue
                    ];
                });
            } else {
                dataValue = data;
            }

            setTableRows(dataValue);
            setLoadingData(false);
        }

    }, [data]);

    function onDateRangeChange(item) {
        if ((item.startDate.date != null && item.endDate.date != null) || (item.startDate.date == null && item.endDate.date == null)) {
            setNoDataMessage("No results found, please try a different date range.");
            optionsRef.current = {
                ...tableOptions,
                startDate: (item.startDate.date ? item.startDate.date.format("YYYY-MM-DD") : ""),
                endDate: (item.endDate.date ? item.endDate.date.format("YYYY-MM-DD") : ""),
                pageNumber: 1
            };
            setTableOptions(optionsRef.current);

            dispatch(SetFiltering(reduxAuditingHistoryName,
                {
                    startDate: optionsRef.current.startDate,
                    endDate: optionsRef.current.endDate
                }
            ));

            setLoadingData(true);
        }
    }

    function onPagingChange(newPageNumber, newPageSize) {
        optionsRef.current = {
            ...tableOptions,
            pageSize: newPageSize,
            pageNumber: newPageNumber
        };
        setTableOptions(optionsRef.current);

        setLoadingData(true);
    }

    return (
        <div className={classNames({ "row": !hideDatesAndPaging })}>
            <ShowHide
                evaluator={hideHeader}
                hide={<h2>{heading}</h2>}
            >
            </ShowHide>
            <ShowHide
                evaluator={hideDatesAndPaging || (tableRows.length === 0 && !loadingData && tableOptions.startDate === "" && tableOptions.endDate === "")}
                hide={(
                    <Fragment>
                        <div className="pull-left listing-table-filter-fields">
                            <FormDateRangeInput
                                onChange={onDateRangeChange}
                                startDate={tableOptions.startDate}
                                endDate={tableOptions.endDate}
                                disabled={loadingData || loading}
                                allowClear={allowClear}
                            ></FormDateRangeInput>
                        </div>
                    </Fragment>
                )}
            >
            </ShowHide>
            <ListingTable
                headers={tableHeaders}
                data={tableRows}
                totalRecordCount={totalCount}
                onPagingChange={onPagingChange}
                loadingData={loadingData || loading}
                noDataMessage={noDataMessageValue}
                pageSize={pageSize}
                showPerPage={!hideDatesAndPaging}
                pageable={!hideDatesAndPaging}
            >
            </ListingTable>
        </div>
    );
}

AuditingTable.propTypes = {
    data: PropTypes.array,
    totalCount: PropTypes.number.isRequired,
    onChange: PropTypes.func.isRequired,
    noDataMessage: PropTypes.string,
    dataIsFormatted: PropTypes.bool,
    tableHeaders: PropTypes.array,
    hideHeader: PropTypes.bool,
    loading: PropTypes.bool,
    pageSize: PropTypes.oneOf(perPageOptions),
    allowClear: PropTypes.bool,
    heading: PropTypes.string,
    hideDatesAndPaging: PropTypes.bool,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    initialStartDate: PropTypes.string,
    initialEndDate: PropTypes.string
};